import React, { useState, useEffect } from 'react';

import {
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';

import { formattedCurrency } from '../../../utils/utils';
import UITablePagination from '../../UI/Table/Pagination/UITablePagination';

const useStyles = makeStyles((theme) => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4, 2),
    textAlign: 'center',
    color: '#94a3b8',
  },
  // Mobile card styles
  mobileCardList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '8px',
  },
  mobileCard: {
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    boxShadow: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#cbd5e1',
      backgroundColor: '#fafbfc',
    },
  },
  mobileCardActive: {
    borderLeft: '4px solid #17497F',
    backgroundColor: '#f0f7ff',
    borderColor: '#bfdbfe',
  },
  mobileCardContentInner: {
    padding: '12px 14px !important',
    '&:last-child': { paddingBottom: '12px !important' },
  },
  mobileCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  mobileCardMetrics: {
    display: 'flex',
    gap: '16px',
    marginTop: '6px',
  },
  mobileMetricItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
  metricLabel: {
    fontSize: '0.68rem',
    color: '#94a3b8',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  metricValue: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#1e293b',
  },
}));

const StockTable = (children) => {
  const { data, loading, error, actualId, doubleClickAction, mobile } = children;
  const classes = useStyles();

  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    setPage(0);
  }, [data]);

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={32} />
      </div>
    );
  }

  if (error || !data || data.length === 0) {
    return (
      <div className={classes.emptyState}>
        <Typography variant="body2">Sem fechamentos encontrados</Typography>
      </div>
    );
  }

  const paginatedData = data.slice(
    page * rowsPerPage,
    rowsPerPage === -1 ? undefined : page * rowsPerPage + rowsPerPage,
  );

  // Mobile: Card view
  if (mobile) {
    return (
      <>
        <div className={classes.mobileCardList}>
          {paginatedData.map((value, _index) => {
            const isActive = value.id === actualId;
            return (
              <Card
                key={value.id}
                className={`${classes.mobileCard} ${isActive ? classes.mobileCardActive : ''}`}
                onClick={() => doubleClickAction(_index)}
              >
                <CardContent className={classes.mobileCardContentInner}>
                  <div className={classes.mobileCardHeader}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600, color: '#1e293b' }}>
                      Vigência #{value.id}
                    </Typography>
                    {!value.dueDate && (
                      <Chip label="Atual" size="small" style={{ backgroundColor: '#dbeafe', color: '#1d4ed8', fontWeight: 600, fontSize: '0.7rem', height: 22 }} />
                    )}
                  </div>
                  <Typography variant="caption" style={{ color: '#64748b' }}>
                    {value.startDate} → {value.dueDate || 'Em aberto'}
                  </Typography>
                  <div className={classes.mobileCardMetrics}>
                    <div className={classes.mobileMetricItem}>
                      <Typography className={classes.metricLabel}>Valor</Typography>
                      <Typography className={classes.metricValue}>{formattedCurrency(value.value)}</Typography>
                    </div>
                    <div className={classes.mobileMetricItem}>
                      <Typography className={classes.metricLabel}>CMV</Typography>
                      <Typography className={classes.metricValue}>{formattedCurrency(value.cmv)}</Typography>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        <UITablePagination
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </>
    );
  }

  // Desktop: Table view (preserved)
  return (
    <TableContainer component={Paper}>
      <Table aria-label="Tabela de fechamentos de estoque">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Valor</TableCell>
            <TableCell>CMV</TableCell>
            <TableCell />
            <TableCell>Início</TableCell>
            <TableCell>Fim</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedData.map((value, _index) => (
            <TableRow
              tabIndex={-1}
              onDoubleClick={() => doubleClickAction(_index)}
              style={{ cursor: 'context-menu', background: value.id === actualId ? '#8ecae6' : '' }}
              hover
              key={value.id}
            >
              <TableCell component="th" scope="row">
                {value.id}
              </TableCell>
              <TableCell>{formattedCurrency(value.value)}</TableCell>
              <TableCell>{formattedCurrency(value.cmv)}</TableCell>
              <TableCell />
              <TableCell>{value.startDate}</TableCell>
              <TableCell>{value.dueDate || 'Em aberto'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <UITablePagination
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </TableContainer>
  );
};

export default StockTable;
